import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PacModeBanner } from "@/components/ops/PacModeBanner";
import {
  FileText, Plus, RefreshCw, Search, ChevronRight, ChevronLeft,
  CheckCircle2, XCircle, Clock, AlertCircle, Download, Eye,
} from "lucide-react";

// ─── Listado de facturas ───────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    timbrado: "bg-green-50 text-green-700 border-green-200",
    timbrado_demo: "bg-amber-50 text-amber-800 border-amber-300",
    borrador: "bg-slate-100 text-slate-600 border-slate-200",
    cancelado: "bg-red-50 text-red-700 border-red-200",
    error: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const labels = { timbrado_demo: "timbrado (demo)" };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${map[status] || map.borrador}`}>
      {labels[status] || status}
    </span>
  );
}

function InvoiceDetailModal({ invoice, onClose, onCancel }) {
  const [canceling, setCanceling] = useState(false);
  const [cancelForm, setCancelForm] = useState({ motivo: "02", folio_sustituye: "" });

  const handleCancel = async () => {
    setCanceling(true);
    try {
      await api.post(`/ops/fiscal/invoices/${invoice.id}/cancel`, cancelForm);
      toast.success("Factura cancelada correctamente");
      onCancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cancelar");
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-start">
          <div>
            <h3 className="font-bold text-slate-900">Factura {invoice.internal_serie}{invoice.internal_folio}</h3>
            <p className="text-xs text-slate-500 font-mono mt-0.5">UUID: {invoice.uuid_fiscal || "—"}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            {[
              ["Estado", <StatusBadge status={invoice.status} />],
              ["Fecha", invoice.fecha_emision?.slice(0, 10)],
              ["Receptor", invoice.receiver?.nombre],
              ["RFC Receptor", invoice.receiver?.rfc],
              ["Forma pago", invoice.forma_pago],
              ["Metodo pago", invoice.metodo_pago],
              ["Subtotal", `$${(invoice.subtotal || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`],
              ["IVA", `$${(invoice.total_traslados || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`],
              ["Total", <strong>${(invoice.total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</strong>],
            ].map(([label, val], i) => (
              <div key={i} className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs text-slate-500">{label}</p>
                <div className="font-medium text-slate-800">{val}</div>
              </div>
            ))}
          </div>

          {invoice.conceptos && (
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Conceptos</p>
              <div className="space-y-1">
                {invoice.conceptos.map((c, i) => (
                  <div key={i} className="flex justify-between text-xs py-1.5 border-b border-slate-100">
                    <span>{c.descripcion} × {c.cantidad}</span>
                    <span className="font-mono">${(c.importe || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {invoice.xml_url && (
            <a href={invoice.xml_url} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 text-xs text-blue-600 hover:underline">
              <Download className="w-3 h-3" />
              Descargar XML timbrado
            </a>
          )}

          {invoice.status === "timbrado" || invoice.status === "timbrado_demo" ? (
            <div className="border-t border-slate-200 pt-4">
              <p className="text-xs font-semibold text-red-700 mb-2">Cancelar factura</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="text-xs text-slate-600 mb-1 block">Motivo</label>
                  <select
                    data-testid="cancel-motivo-select"
                    value={cancelForm.motivo}
                    onChange={e => setCancelForm(f => ({ ...f, motivo: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs"
                  >
                    <option value="01">01 — Errores con relacion</option>
                    <option value="02">02 — Errores sin relacion</option>
                    <option value="03">03 — No se llevo a cabo</option>
                    <option value="04">04 — Operacion nominativa</option>
                  </select>
                </div>
                {cancelForm.motivo === "01" && (
                  <div>
                    <label className="text-xs text-slate-600 mb-1 block">UUID sustituto</label>
                    <input
                      data-testid="cancel-sustituye-input"
                      value={cancelForm.folio_sustituye}
                      onChange={e => setCancelForm(f => ({ ...f, folio_sustituye: e.target.value }))}
                      className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-xs font-mono"
                      placeholder="UUID del CFDI sustituto"
                    />
                  </div>
                )}
              </div>
              <button
                data-testid="btn-confirmar-cancelacion"
                onClick={handleCancel}
                disabled={canceling}
                className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
              >
                {canceling && <RefreshCw className="w-3 h-3 animate-spin" />}
                Confirmar cancelacion
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function OpsFiscalFacturacion() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, skip: page * PAGE_SIZE });
      if (filter.status) params.append("status", filter.status);
      const res = await api.get(`/ops/fiscal/invoices?${params}`);
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error("Error al cargar facturas");
    } finally {
      setLoading(false);
    }
  }, [page, filter.status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <PacModeBanner />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Facturacion electronica</h2>
          <p className="text-sm text-slate-500">{total} factura{total !== 1 ? "s" : ""} registrada{total !== 1 ? "s" : ""}</p>
        </div>
        <button
          data-testid="btn-nueva-factura-list"
          onClick={() => navigate("/ops/fiscal/facturacion/nueva")}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nueva factura
        </button>
      </div>

      {/* Filtros */}
      <div className="flex gap-2">
        <select
          data-testid="filter-status"
          value={filter.status}
          onChange={e => { setFilter(f => ({ ...f, status: e.target.value })); setPage(0); }}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="timbrado">Timbrado</option>
          <option value="timbrado_demo">Timbrado (demo)</option>
          <option value="cancelado">Cancelado</option>
          <option value="borrador">Borrador</option>
          <option value="error">Error</option>
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />Cargando...
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No hay facturas</p>
          <p className="text-sm text-slate-400 mt-1">Emiti tu primer CFDI 4.0 desde el wizard</p>
          <button
            onClick={() => navigate("/ops/fiscal/facturacion/nueva")}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium"
          >
            Emitir primera factura
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Folio", "Fecha", "Receptor", "RFC", "Total", "Estado", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs">
                    {inv.internal_serie}{inv.internal_folio}
                    {inv.is_demo && (
                      <span data-testid={`invoice-demo-tag-${inv.id}`} className="ml-1 text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1">
                        DEMO
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{inv.fecha_emision?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-xs max-w-[160px] truncate">{inv.receiver?.nombre}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{inv.receiver?.rfc}</td>
                  <td className="px-4 py-3 text-xs font-medium">
                    ${(inv.total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`btn-ver-factura-${inv.id}`}
                      onClick={() => setSelected(inv)}
                      className="text-blue-600 hover:underline text-xs flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />Ver
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Paginacion */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
              <span>Mostrando {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} de {total}</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="disabled:opacity-40">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} className="disabled:opacity-40">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {selected && (
        <InvoiceDetailModal
          invoice={selected}
          onClose={() => setSelected(null)}
          onCancel={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}
